<template>
  <v-card elevation="0" outlined class="d-flex flex-column h-100" min-height="350">
    <v-card-title class="pt-1 pb-1 white--text d-flex align-center justify-space-between"
      :class="$vuetify.theme.dark ? 'black' : 'primary'">
      <div class="d-flex align-center">
        <v-icon color="white" class="mr-2" medium>list</v-icon>
        <span class="white--text body-font body-1 font-weight-bold text-uppercase">{{
          $t("scoutingCalendar.tasks.myTasks") }}</span>
      </div>
      <v-btn class="pl-0 pr-0" text small :to="`/tasks-board`">
        <span class="white--text text-decoration-underline font-weight-bold">{{ $t('general.seeAll') }}</span>
        <v-icon small class="ml-1" color="white">keyboard_arrow_right</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-text class="d-flex flex-column pb-1 flex-grow-1 pa-0">
      <template v-if="!loading">
        <!--Tabs-->
        <div class="grey lighten-4 tabs-subheader-container">
          <v-btn-toggle v-model="tabModel" tile group dense mandatory class="tabs-subheader d-flex w-100">
            <v-btn :value="0" class="flex-grow-1">
              <span class="body-2 font-weight-bold text-uppercase">{{ $t('general.today') }}</span>
            </v-btn>
            <v-btn :value="1" class="flex-grow-1">
              <span class="body-2 font-weight-bold text-uppercase">{{ $t('scoutingTasksBoard.period.week') }}</span>
            </v-btn>
          </v-btn-toggle>
        </div>
        <!--Items-->
        <div style="background:transparent" class="overflow-auto h-100 px-3 pt-2">
          <!--Today-->
          <div v-if="tabModel === 0" class="transparent">
            <v-data-table dense disable-items-per-page multi-sort :headers="headers" :items="todayTasks"
              :footer-props="{ itemsPerPageOptions: $vuetify.breakpoint.smAndDown ? [1] : [5] }" sort-by="start"
              :sort-desc="true" :no-data-text="$t('scoutingCalendar.tasks.noData')" class="pa-0">
              <!--Name-->
              <template v-slot:item.name="{ item }">
                <router-link class="py-2 d-flex align-center justify-space-between nav-link"
                  :to="`/tasks-board?id=${item.id}`">
                  <span class="text-ellipsis w-90 body-1">
                    <v-tooltip top color="lightSecondary lighten-4" v-if="item.typeId && item.typeId !== 3"
                      max-width="300">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-on="on" v-bind="attrs" small>{{ getEventTypeIcon(item) }}</v-icon>
                      </template>
                      <span class="black--text body-1">{{ getEventTypeText(item) }}</span>
                    </v-tooltip>
                    {{ item.name }}
                  </span>
                </router-link>
              </template>
              <!--State-->
              <template v-slot:item.boardStateId="{ item }">
                <v-chip small :color="getEventStateColor(item)" class="white--text monospace-font">{{
                  getEventStateText(item) }}</v-chip>
              </template>
              <!--Assigned users-->
              <template v-slot:item.userAssignedIds="{ item }">
                <v-tooltip top color="lightSecondary lighten-4" v-if="item.userAssignedIds" max-width="300">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-on="on" v-bind="attrs" small>person</v-icon>
                  </template>
                  <span class="black--text body-1">{{ item.userAssignedNames }}</span>
                </v-tooltip>
                <span v-else>-</span>
              </template>
            </v-data-table>
          </div>
          <!--This week-->
          <div v-if="tabModel === 1" class="transparent">
            <v-data-table dense disable-items-per-page multi-sort :headers="headers" :items="thisWeekTasks"
              :footer-props="{ itemsPerPageOptions: $vuetify.breakpoint.smAndDown ? [1] : [5] }" sort-by="start"
              :sort-desc="true" :no-data-text="$t('scoutingCalendar.tasks.noData')" class="pa-0">
              <!--Name-->
              <template v-slot:item.name="{ item }">
                <router-link class="py-2 d-flex align-center justify-space-between nav-link"
                  :to="`/tasks-board?id=${item.id}`">
                  <span class="text-ellipsis w-90">
                    <v-tooltip top color="blue-grey lighten-5" v-if="item.typeId && item.typeId !== 3" max-width="300">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-on="on" v-bind="attrs" x-small>{{ getEventTypeIcon(item) }}</v-icon>
                      </template>
                      <span class="black--text body-1">{{ getEventTypeText(item) }}</span>
                    </v-tooltip>
                    {{ item.name }}
                  </span>
                </router-link>
              </template>
              <!--State-->
              <template v-slot:item.boardStateId="{ item }">
                <v-chip x-small :color="getEventStateColor(item)" class="white--text">{{ getEventStateText(item)
                  }}</v-chip>
              </template>
              <!--Assigned users-->
              <template v-slot:item.userAssignedIds="{ item }">
                <v-tooltip top color="blue-grey lighten-5" v-if="item.userAssignedIds" max-width="300">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-on="on" v-bind="attrs" x-small>person</v-icon>
                  </template>
                  <span class="black--text body-1">{{ item.userAssignedNames }}</span>
                </v-tooltip>
                <span v-else>-</span>
              </template>
            </v-data-table>
          </div>
        </div>
      </template>

      <!-- Skeleton Loader -->
      <template v-else>
        <div class="mt-4 pl-6 pr-4 d-flex align-center justify-space-between">
          <v-skeleton-loader type="text" width="30%" class="ma-0"></v-skeleton-loader>
          <v-skeleton-loader type="text" width="15%" class="ma-0"></v-skeleton-loader>
        </div>
        <div class="px-6 py-4 flex-grow-1 d-flex flex-column justify-space-around">
          <v-skeleton-loader v-for="i in 4" :key="i" type="text" width="100%" class="my-2"></v-skeleton-loader>
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import moment from 'moment';
import CalendarService from '../../../services/calendar.service';

export default {
  name: "ScoutingTasksWidget",
  data() {
    return {
      todayTasks: [],
      thisWeekTasks: [],
      tabModel: 0,
      loading: false,
      today: moment().format('YYYY-MM-DD'),
      tomorrow: moment().add(1, 'days').format('YYYY-MM-DD'),
      dateFrom: moment().startOf('week').format('YYYY-MM-DD'),
      dateTo: moment().endOf('week').format('YYYY-MM-DD'),
      headers: [
        {
          text: this.$t("playerDetail.name"),
          value: "name",
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("general.state"),
          value: "boardStateId",
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("scoutingCalendar.tasks.assignedTo"),
          value: "userAssignedIds",
          align: "center",
          sortable: false,
        },
      ]
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
  },
  async created() {
    this.loading = true;
    await this.getTodayTasks();
    await this.getThisWeekTasks();
    this.loading = false;
  },
  methods: {
    async getTodayTasks() {
      const todayData = await CalendarService.getCalendarEventsByUserIdAndDates(this.currentUser.id, this.today, this.today);
      this.todayTasks = todayData.data;
      const todayTasksIds = this.todayTasks.map(t => t.id);
      //Map users assigned to each task
      if (this.todayTasks.length) {
        const assignedUsersData = await CalendarService.getUsersAssignedByEventCalendarIds(todayTasksIds);
        this.todayTasks = this.formatTasks(this.todayTasks, assignedUsersData);
      };
    },
    async getThisWeekTasks() {
      const thisWeekData = await CalendarService.getCalendarEventsByUserIdAndDates(this.currentUser.id, this.dateFrom, this.dateTo);
      this.thisWeekTasks = thisWeekData.data;
      const weekTasksIds = this.thisWeekTasks.map(t => t.id);
      //Map users assigned to each task
      if (this.thisWeekTasks.length) {
        const assignedUsersData = await CalendarService.getUsersAssignedByEventCalendarIds(weekTasksIds);
        this.thisWeekTasks = this.formatTasks(this.thisWeekTasks, assignedUsersData);
      };
    },
    getEventStateColor(e) {
      switch (e.boardStateId) {
        case 1:
          return 'red';
        case 2:
          return 'amber';
        case 3:
          return 'green';
        default:
          return 'primary';
      };
    },
    getEventStateText(e) {
      switch (e.boardStateId) {
        case 1:
          return this.$t('scoutingCalendar.tasks.states.toDo');
        case 2:
          return this.$t('scoutingCalendar.tasks.states.inProgress');
        case 3:
          return this.$t('scoutingCalendar.tasks.states.done');
        default:
          return this.$t('scoutingCalendar.tasks.states.toDo');
      };
    },
    getEventTypeIcon(e) {
      switch (e.typeId) {
        case 1:
          return 'directions_run';
        case 2:
          return 'shield';
        case 3:
          return 'event';
        default:
          return 'event';
      };
    },
    getEventTypeText(e) {
      switch (e.typeId) {
        case 1:
          return this.$t('scoutingCalendar.tasks.type.player');
        case 2:
          return this.$t('scoutingCalendar.tasks.type.team');
        case 3:
          return this.$t('scoutingCalendar.tasks.type.other');
        default:
          return this.$t('scoutingCalendar.tasks.type.other');
      };
    },
    formatTasks(tasksArr, assignedUsersData) {
      return tasksArr.map(e => {
        e.assignedUsers = assignedUsersData.data.length ? assignedUsersData.data.filter(u => u.eventId === e.id) : [];
        e.userAssignedIds = e.assignedUsers.length ? e.assignedUsers.map(u => u.id) : null;
        e.userAssignedNames = e.assignedUsers.length ? e.assignedUsers.map(u => `${u.firstName} ${u.lastName}`).join(", ") : null;
        return e;
      });
    }
  }
};
</script>

<style scoped>
/* ============================================
   Header and Container Borders
   ============================================ */
.scouting-tasks-widget .header-with-border {
  border-bottom: 2px solid white;
}

.scouting-tasks-widget .tabs-subheader-container {
  border-top: 2px solid white;
}

/* ============================================
   Button Toggle Styling
   ============================================ */
.scouting-tasks-widget .v-btn-toggle.tabs-subheader {
  background-color: transparent;
  box-shadow: none;
}

.scouting-tasks-widget .v-btn-toggle.tabs-subheader .v-btn {
  border-radius: 0;
  text-transform: none;
  margin: 0;
  padding: 12px 16px;
  flex: 1;
  min-width: 0;
}

/* Active button (selected) */
.scouting-tasks-widget .v-btn-toggle.tabs-subheader .v-btn--active {
  background-color: rgba(31, 41, 55, 0.8) !important;
  color: white !important;
}

.scouting-tasks-widget .v-btn-toggle.tabs-subheader .v-btn--active * {
  color: white !important;
  font-weight: bold !important;
}

/* Inactive button (unselected) */
.scouting-tasks-widget .v-btn-toggle.tabs-subheader .v-btn:not(.v-btn--active) {
  background-color: rgba(0, 0, 0, 0.1) !important;
  color: black !important;
}

.scouting-tasks-widget .v-btn-toggle.tabs-subheader .v-btn:not(.v-btn--active) * {
  color: black !important;
  font-weight: bold !important;
}
</style>